/* PreviewDetailPage.jsx — ONE unified detail page for every kept preview.
 *
 * Replaces 7 path-specific detail pages (AIRoleDetail, BTechPreviewDetail,
 * BankingRoleDetail, etc.) with a single component that reads the normalised
 * shape from `/api/previews/:path_key/:preview_id`:
 *
 *   { id, path_key, title, subtitle, summary,
 *     what_youd_do[], skills_needed[], indicative_pay, raw }
 *
 * Security: the endpoint already 403s if the preview isn't in the user's picks.
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Briefcase, Loader2, Sparkles, IndianRupee, ListChecks } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { useAuth } from '../lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;

const PATH_LABEL = {
  job_roles: 'Future-Ready Job Roles',
  ai_pathway: 'AI Pathway',
  specializations: 'Inner Specializations',
  banking_bluecollar: 'Banking Careers',
  entrepreneurship: 'Entrepreneurship · OwnPath',
  after_10th_inter: 'Career Clarity Tests — After 10th / Inter',
};

export default function PreviewDetailPage() {
  const { path_key, preview_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user === null) return;
    if (user === false) { navigate('/signin', { replace: true }); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/api/previews/${path_key}/${preview_id}`);
        setPreview(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Could not load preview.');
      } finally { setLoading(false); }
    })();
  }, [user, navigate, path_key, preview_id]);

  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50">
        <Loader2 className="w-10 h-10 animate-spin text-teal-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
        <Navbar />
        <div className="pt-28 pb-16 px-4">
          <div className="max-w-2xl mx-auto bg-white border border-rose-200 rounded-3xl p-8 text-center" data-testid="preview-error">
            <h2 className="text-lg font-bold text-gray-900 mb-2">This preview isn't in your picks</h2>
            <p className="text-sm text-gray-600 mb-4">{error}</p>
            <Button asChild className="btn-primary">
              <Link to="/my/path">Back to my path</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!preview) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      <Navbar />
      <div className="pt-28 pb-16 px-4">
        <div className="max-w-3xl mx-auto">

          <Link
            to={preview.path_key === 'after_10th_inter' ? '/dashboard' : '/my/path'}
            className="inline-flex items-center text-sm font-semibold text-gray-700 hover:text-teal-700 mb-5"
            data-testid="preview-back"
          >
            <ArrowLeft size={14} className="mr-1.5" /> Back
          </Link>

          {/* Hero */}
          <div className="bg-gradient-to-br from-teal-600 to-blue-700 rounded-3xl p-7 md:p-9 text-white shadow-xl mb-6"
               data-testid="preview-hero">
            <div className="text-white/70 text-[11px] uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Briefcase size={11} /> {PATH_LABEL[preview.path_key] || preview.path_key}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold leading-tight mb-2" data-testid="preview-title">
              {preview.title}
            </h1>
            {preview.subtitle && (
              <p className="text-white/85 text-sm font-medium" data-testid="preview-subtitle">
                {preview.subtitle}
              </p>
            )}
            {preview.indicative_pay && (
              <div className="mt-4 inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold"
                   data-testid="preview-pay">
                <IndianRupee size={11} /> {preview.indicative_pay}
              </div>
            )}
          </div>

          {/* Summary */}
          {preview.summary && (
            <div className="bg-white border border-gray-200 rounded-3xl p-6 md:p-8 shadow-sm mb-5">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5">
                <Sparkles size={11} /> If you read nothing else
              </div>
              <p className="text-gray-800 leading-relaxed text-[15px]" data-testid="preview-summary">
                {preview.summary}
              </p>
            </div>
          )}

          {/* What you'd do */}
          {preview.what_youd_do?.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-3xl p-6 md:p-8 shadow-sm mb-5">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-1.5">
                <ListChecks size={11} /> A day in this role
              </div>
              <ul className="space-y-2.5">
                {preview.what_youd_do.map((line, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-gray-800" data-testid={`preview-task-${i}`}>
                    <span className="mt-1.5 block w-1.5 h-1.5 rounded-full bg-teal-500 flex-shrink-0" />
                    <span>{typeof line === 'string' ? line : JSON.stringify(line)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Skills */}
          {preview.skills_needed?.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-3xl p-6 md:p-8 shadow-sm mb-5">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-3">Skills you'd build</div>
              <div className="flex flex-wrap gap-2">
                {preview.skills_needed.map((s, i) => (
                  <span key={i} className="bg-teal-50 text-teal-800 border border-teal-200 rounded-full px-3 py-1 text-xs font-semibold"
                        data-testid={`preview-skill-${i}`}>{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* Path-specific extras can use preview.raw without breaking the page */}
          {preview.path_key === 'after_10th_inter' && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-3xl p-6 md:p-8 mb-5">
              <h3 className="font-bold text-amber-900 mb-2">Take your clarity quiz</h3>
              <p className="text-sm text-amber-800 mb-4">
                For this track, your clarity comes from a short quiz, not a content lock.
                You have 3 attempts.
              </p>
              <Button asChild className="bg-amber-600 hover:bg-amber-700 text-white">
                <Link to={`/career-clarity/${preview.id === 'btech' ? 'btech' : 'bba-mba'}`}>
                  Start quiz
                </Link>
              </Button>
            </div>
          )}

          <div className="text-center mt-8">
            <Button asChild variant="outline" data-testid="preview-back-bottom">
              <Link to={preview.path_key === 'after_10th_inter' ? '/dashboard' : '/my/path'}>
                Back to my path
              </Link>
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
}
